import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import Select from 'react-select';
import { set, cloneDeep, unionBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { customStyles, transportArray } from '../../../Components/helper';
import * as systemMasterAction from '../../SystemMasters/Store/SystemMasters.action';
import * as supplierAction from '../../Supplier/store/Supplier.action';
import SystemMastersType from '../../SystemMasters/Store/SystemMasters.type';

const getInitialState = () => ({
  tierOne: null,
  tierTwo: {
    postProcessingAnotherLocation: 'no',
    finishedProductAssemblySupp: 'componentSupplier',
    componentSupplierObj: {
      componentSupplier: null,
      energySource: '',
      energyIntensity: '',
      transport: null,
      bomProcessType: null,
    },
    componentPostProcessing: {
      energySource: '',
      energyIntensity: '',
      transport: null,
      postProcessingSupp: null,
      bomProcessType: null,
    },
  },
  tierThree: {
    rawMaterialProcessorExtractor: 'yes',
    rawMaterialProcessor: null,
    energySource: '',
    energyIntensity: '',
    transport: null,
    bomProcessType: null,
  },
  tierFour: {
    rawMaterialExtractor: null,
    energySource: '',
    energyIntensity: '',
    transport: null,
    compositionProcessType: null,
  },
});

const BomProductImpact = forwardRef(
  (
    {
      referenceSupp,
      combinedDataArray,
      isCombinationSupplierActive,
      BomSuppliers,
      id,
      isFromLibrary,
      bomDetailsBasedOnId,
      bomSubType,
      bomTypeId,
      fromBomExpand,
      isSuggestionMode,
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const reducerState = useSelector((redState) => redState);
    const [state, setState] = useState(getInitialState());
    const previousIdRef = useRef(id);
    const SelectTierStyle = {
      option: (provided) => ({
        ...provided,
        padding: '2px 8px',
      }),
      control: () => ({
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '24px',
        border: '1px solid #6a6a6a',
        padding: '2px 6px',
      }),
      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.7 : 1;
        const transition = 'opacity 300ms';

        return { ...provided, opacity, transition, color: '#000' };
      },
      menu: (provided) => ({
        ...provided,
        margin: '0',
        border: '1px solid #6a6a6a',
        borderRadius: 0,
        fontSize: 14,
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        color: '#000',
        fontFamily: 'Aktiv Grotesk, sans-serif !important',
      }),
      placeholder: (provided) => ({
        ...provided,
        color: '#000 !important',
        fontSize: 16,
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: '#aaa', // Custom colour
        padding: '8px 2px',
        svg: {
          width: '22px',
          height: '22px',
        },
      }),
    };
    const {
      tierTwo: { componentSupplierObj, componentPostProcessing, postProcessingAnotherLocation, finishedProductAssemblySupp },
      tierThree,
      tierFour,
      tierOne,
    } = state;
    const isReadOnly = !!fromBomExpand;
    const { BomLibraryDetails, SuppliersDetails, SystemMastersDetails, UserDetails } = reducerState;
    const { userInfo } = UserDetails || {};
    const { ReferenceSupplier, BOMLibraryList } = BomLibraryDetails;
    const { tier4Suppliers, tier3Suppliers } = SuppliersDetails;

    const bomComponentTypeData = useMemo(() => SystemMastersDetails?.bomComponentTypeData || [], [SystemMastersDetails?.bomComponentTypeData]);
    const componentTypeOptions = useMemo(() => {
      const list = bomComponentTypeData.filter((opt) => opt.tier === 'tier2' && opt.isActive === true);
      return list.map((opt) => ({ label: opt?.name, value: opt?._id }));
    }, [bomComponentTypeData]);

    const tierThreeComponentTypeOptions = useMemo(() => {
      const list = bomComponentTypeData.filter((opt) => opt.tier === 'tier3' && opt.isActive === true);
      return list.map((opt) => ({ label: opt?.name, value: opt?._id }));
    }, [bomComponentTypeData]);

    const allBomComponentTypeData = useMemo(() => {
      return bomComponentTypeData.reduce((acc, opt) => {
        acc[opt._id] = opt;
        return acc;
      }, {});
    }, [bomComponentTypeData]);

    const getBomProcessTypeIds = (bomProcessType) => {
      if (!Array.isArray(bomProcessType) || bomProcessType.length === 0) return [];
      return bomProcessType
        .map((item) => {
          if (typeof item === 'string') return item;
          if (typeof item === 'object' && item !== null) return item._id || item;
          return null;
        })
        .filter(Boolean);
    };

    const selectedTierThreeComponentTypes = useMemo(() => {
      const bomProcessTypeIds = getBomProcessTypeIds(tierThree?.bomProcessType);
      const selectedOptions = bomProcessTypeIds
        .map((id) => {
          const data = allBomComponentTypeData[id];
          if (data && data.tier === 'tier3') {
            return { label: data.name, value: data._id };
          }
          return null;
        })
        .filter(Boolean);
      return selectedOptions || [];
    }, [tierThree?.bomProcessType, allBomComponentTypeData]);

    const selectedTierTwoComponentSupplierTypes = useMemo(() => {
      const bomProcessTypeIds = getBomProcessTypeIds(componentSupplierObj?.bomProcessType);
      const selectedOptions = bomProcessTypeIds
        .map((id) => {
          const data = allBomComponentTypeData[id];
          if (data && data.tier === 'tier2') {
            return { label: data.name, value: data._id };
          }
          return null;
        })
        .filter(Boolean);
      return selectedOptions || [];
    }, [componentSupplierObj?.bomProcessType, allBomComponentTypeData]);

    const selectedTierTwoComponentPostProcessingTypes = useMemo(() => {
      const bomProcessTypeIds = getBomProcessTypeIds(componentPostProcessing?.bomProcessType);
      const selectedOptions = bomProcessTypeIds
        .map((id) => {
          const data = allBomComponentTypeData[id];
          if (data && data.tier === 'tier2') {
            return { label: data.name, value: data._id };
          }
          return null;
        })
        .filter(Boolean);
      return selectedOptions || [];
    }, [componentPostProcessing?.bomProcessType, allBomComponentTypeData]);

    useEffect(() => {
      if (previousIdRef.current !== id && previousIdRef.current !== null && previousIdRef.current !== undefined) {
        setState(getInitialState());
        dispatch({
          type: SystemMastersType.GET_BOM_COMPONENT_TYPE,
          payload: [],
        });
        dispatch({
          type: SystemMastersType.GET_COMPOSITION_PROCESS,
          payload: [],
        });
      }
      previousIdRef.current = id;
    }, [id, dispatch]);

    useEffect(() => {
      return () => {
        setState(getInitialState());
        dispatch({
          type: SystemMastersType.GET_BOM_COMPONENT_TYPE,
          payload: [],
        });
        dispatch({
          type: SystemMastersType.GET_COMPOSITION_PROCESS,
          payload: [],
        });
      };
    }, [dispatch]);

    useEffect(() => {
      if (id) {
        if (isFromLibrary) {
          const findEditModeData = BOMLibraryList?.find((vl) => vl._id === id);
          if (findEditModeData?.productImpact) {
            setState((prev) => ({ ...prev, ...findEditModeData.productImpact }));
          }
        } else if (bomDetailsBasedOnId?.productImpact) {
          setState((prev) => ({ ...prev, ...bomDetailsBasedOnId.productImpact }));
        }
      } else if (!id && !isSuggestionMode) {
        setState(getInitialState());
      }
    }, [id, BOMLibraryList, isFromLibrary, bomDetailsBasedOnId?.productImpact, isSuggestionMode]);

    useEffect(() => {
      if (isSuggestionMode && bomDetailsBasedOnId?.productImpact) {
        setState((prev) => ({ ...prev, ...bomDetailsBasedOnId.productImpact }));
      } else if (isSuggestionMode && !bomDetailsBasedOnId?.productImpact) {
        setState(getInitialState());
      }
    }, [bomDetailsBasedOnId?.productImpact, isSuggestionMode]);

    useEffect(() => {
      if (isSuggestionMode) {
        const fetchTierSupplierList = async () => {
          await dispatch(supplierAction.fetchTierSSupplierListAction({ isWithFactory: true, tierType: 'Tier2' }));
          await dispatch(supplierAction.fetchTierSSupplierListAction({ isWithFactory: true, tierType: 'Tier3' }));
          await dispatch(supplierAction.fetchTierSSupplierListAction({ isWithFactory: true, tierType: 'Tier4' }));
          await dispatch(supplierAction.fetchTierSSupplierListAction({ isWithFactory: true, tierType: 'Tier1' }));
        };
        fetchTierSupplierList();
      }
    }, [isSuggestionMode, dispatch]);

    useEffect(() => {
      if (bomTypeId) {
        setState((prev) => ({
          ...prev,
          bomComponent: {
            ...prev.bomComponent,
            bomTypeId,
            tier: 'tier2',
          },
        }));
      }
    }, [bomTypeId]);

    // Load Component Types when BOM Type and Component Type are available
    useEffect(() => {
      const bomSubTypeId = bomSubType?._id;
      if (bomTypeId && bomSubTypeId) {
        const params = {
          bomTypeId,
          bomSubTypeId,
          loggedInUserId: userInfo?.userId,
        };
        dispatch(systemMasterAction.getBomComponentTypeDetails(params));
      }
    }, [dispatch, bomTypeId, bomSubType?._id, userInfo?.userId]);

    useEffect(() => {
      const options = SystemMastersDetails?.bomComponentTypeData || [];
      if (!options.length) return;

      setState((prev) => {
        const curreentInTiewTwoComponentSupplier = prev?.tierTwo?.componentSupplierObj?.bomProcessType;
        const curreentInTierTwoComponentPostProcessing = prev?.tierTwo?.componentPostProcessing?.bomProcessType;
        const curreentInTierThree = prev?.tierThree?.bomProcessType;
        const migrated = prev?.bomComponent?.componentTypeIds;
        const nextValues =
          Array.isArray(migrated) && migrated.length > 0
            ? migrated
            : options.filter((opt) => opt.tier === 'tier2' && opt.isActive === true).map((opt) => opt._id);
        const next = cloneDeep(prev);
        if (!curreentInTierThree?.length) {
          const nextValuesTierThree = options?.filter((opt) => opt.tier === 'tier3' && opt.isActive === true)?.map((opt) => opt._id) || [];
          set(next, 'tierThree.bomProcessType', nextValuesTierThree);
        }
        if (!curreentInTiewTwoComponentSupplier?.length) {
          set(next, 'tierTwo.componentSupplierObj.bomProcessType', nextValues);
        }
        if (!curreentInTierTwoComponentPostProcessing?.length) {
          set(next, 'tierTwo.componentPostProcessing.bomProcessType', nextValues);
        }
        return next;
      });
    }, [SystemMastersDetails?.bomComponentTypeData]);

    useEffect(() => {
      const options = SystemMastersDetails?.compositionMasterData || [];
      if (!options.length) return;

      setState((prev) => {
        const nextValues = options?.filter((item) => item.isActive === true)?.map((item) => item?._id) || [];
        const next = cloneDeep(prev);
        const curreentInTierFourCompositionProcessType = prev?.tierFour?.compositionProcessType;
        if (!curreentInTierFourCompositionProcessType?.length) {
          set(next, 'tierFour.compositionProcessType', nextValues);
        }
        return next;
      });
    }, [SystemMastersDetails?.compositionMasterData]);

    const transformStateToIds = (stateData) => {
      const transformed = cloneDeep(stateData);

      if (transformed.tierOne) {
        transformed.tierOne =
          typeof transformed.tierOne === 'object' && transformed.tierOne !== null ? transformed.tierOne._id || transformed.tierOne : transformed.tierOne;
      }

      if (transformed.tierTwo) {
        if (transformed.tierTwo.componentSupplierObj) {
          if (transformed.tierTwo.componentSupplierObj.componentSupplier) {
            transformed.tierTwo.componentSupplierObj.componentSupplier =
              typeof transformed.tierTwo.componentSupplierObj.componentSupplier === 'object' &&
              transformed.tierTwo.componentSupplierObj.componentSupplier !== null
                ? transformed.tierTwo.componentSupplierObj.componentSupplier._id || transformed.tierTwo.componentSupplierObj.componentSupplier
                : transformed.tierTwo.componentSupplierObj.componentSupplier;
          }
          if (Array.isArray(transformed.tierTwo.componentSupplierObj.bomProcessType)) {
            transformed.tierTwo.componentSupplierObj.bomProcessType = transformed.tierTwo.componentSupplierObj.bomProcessType.map((item) =>
              typeof item === 'object' && item !== null ? item._id || item : item,
            );
            if (transformed.tierTwo.componentSupplierObj.bomProcessType.length === 0) {
              delete transformed.tierTwo.componentSupplierObj.bomProcessType;
            }
          }
        }
        if (transformed.tierTwo.componentPostProcessing) {
          if (transformed.tierTwo.componentPostProcessing.postProcessingSupp) {
            transformed.tierTwo.componentPostProcessing.postProcessingSupp =
              typeof transformed.tierTwo.componentPostProcessing.postProcessingSupp === 'object' &&
              transformed.tierTwo.componentPostProcessing.postProcessingSupp !== null
                ? transformed.tierTwo.componentPostProcessing.postProcessingSupp._id || transformed.tierTwo.componentPostProcessing.postProcessingSupp
                : transformed.tierTwo.componentPostProcessing.postProcessingSupp;
          }
          if (Array.isArray(transformed.tierTwo.componentPostProcessing.bomProcessType)) {
            transformed.tierTwo.componentPostProcessing.bomProcessType = transformed.tierTwo.componentPostProcessing.bomProcessType.map((item) =>
              typeof item === 'object' && item !== null ? item._id || item : item,
            );
            if (transformed.tierTwo.componentPostProcessing.bomProcessType.length === 0) {
              delete transformed.tierTwo.componentPostProcessing.bomProcessType;
            }
          }
        }
      }

      if (transformed.tierThree) {
        if (transformed.tierThree.rawMaterialProcessor) {
          transformed.tierThree.rawMaterialProcessor =
            typeof transformed.tierThree.rawMaterialProcessor === 'object' && transformed.tierThree.rawMaterialProcessor !== null
              ? transformed.tierThree.rawMaterialProcessor._id || transformed.tierThree.rawMaterialProcessor
              : transformed.tierThree.rawMaterialProcessor;
        }
        if (Array.isArray(transformed.tierThree.bomProcessType)) {
          transformed.tierThree.bomProcessType = transformed.tierThree.bomProcessType.map((item) =>
            typeof item === 'object' && item !== null ? item._id || item : item,
          );
          if (transformed.tierThree.bomProcessType.length === 0) {
            delete transformed.tierThree.bomProcessType;
          }
        }
      }

      if (transformed.tierFour) {
        if (transformed.tierFour.rawMaterialExtractor) {
          transformed.tierFour.rawMaterialExtractor =
            typeof transformed.tierFour.rawMaterialExtractor === 'object' && transformed.tierFour.rawMaterialExtractor !== null
              ? transformed.tierFour.rawMaterialExtractor._id || transformed.tierFour.rawMaterialExtractor
              : transformed.tierFour.rawMaterialExtractor;
        }
        if (Array.isArray(transformed.tierFour.compositionProcessType)) {
          transformed.tierFour.compositionProcessType = transformed.tierFour.compositionProcessType.map((item) =>
            typeof item === 'object' && item !== null ? item._id || item : item,
          );
          if (transformed.tierFour.compositionProcessType.length === 0) {
            delete transformed.tierFour.compositionProcessType;
          }
        }
      }

      return transformed;
    };

    useImperativeHandle(ref, () => ({
      getBomImpactLocalState: () => transformStateToIds(state),
    }));
    const handleChange = (value, path, _cloneObj) => {
      if (fromBomExpand) return;
      const cloneState = cloneDeep({ ...state });
      set(cloneState, path, value);
      switch (path) {
        case 'tierTwo.componentSupplierObj.componentSupplier':
          set(cloneState, 'tierTwo.componentSupplierObj.energySource', _cloneObj?.factory?.[0]?.energySource?.value || '');
          set(cloneState, 'tierTwo.componentSupplierObj.energyIntensity', _cloneObj?.factory?.[0]?.energyIntensity || '');
          break;
        case 'tierTwo.componentPostProcessing.postProcessingSupp':
          set(cloneState, 'tierTwo.componentPostProcessing.energySource', _cloneObj?.factory?.[0]?.energySource?.value || '');
          set(cloneState, 'tierTwo.componentPostProcessing.energyIntensity', _cloneObj?.factory?.[0]?.energyIntensity || '');
          break;
        case 'tierThree.rawMaterialProcessor':
          set(cloneState, 'tierThree.energySource', _cloneObj?.factory?.[0]?.energySource?.value || '');
          set(cloneState, 'tierThree.energyIntensity', _cloneObj?.factory?.[0]?.energyIntensity || '');
          break;
        case 'tierFour.rawMaterialExtractor':
          set(cloneState, 'tierFour.energySource', _cloneObj?.factory?.[0]?.energySource?.value || '');
          set(cloneState, 'tierFour.energyIntensity', _cloneObj?.factory?.[0]?.energyIntensity || '');
          break;
        case 'tierTwo.postProcessingAnotherLocation':
          set(cloneState, 'tierTwo.componentPostProcessing', {
            energySource: '',
            energyIntensity: '',
            transport: null,
            postProcessingSupp: null,
          });
          break;
        default:
          break;
      }
      setState((prev) => ({ ...prev, ...cloneState }));
    };

    const componentAndPrdocutOptions = useMemo(() => {
      if (isCombinationSupplierActive) {
        const filterCombination = combinedDataArray?.filter((comb) => comb.componentSupplier && comb.productSupplier);
        if (filterCombination?.length) {
          let componentSupplier = filterCombination?.filter((vl) => vl?.componentSupplier)?.map((vl) => vl?.componentSupplier);
          let productSupplier = filterCombination?.filter((vl) => vl?.productSupplier)?.map((vl) => vl?.productSupplier);
          componentSupplier = unionBy(componentSupplier, '_id');
          productSupplier = unionBy(productSupplier, '_id');
          return { componentSupplier, productSupplier };
        }
      }
      return { componentSupplier: [], productSupplier: [] };
    }, [combinedDataArray, isCombinationSupplierActive]);

    const tier2SupplierOptions = useMemo(() => {
      if (referenceSupp?.length > 0 && !isCombinationSupplierActive) {
        return referenceSupp;
      }
      if (isCombinationSupplierActive && componentAndPrdocutOptions?.componentSupplier?.length) {
        return componentAndPrdocutOptions?.componentSupplier;
      }
      return [];
    }, [referenceSupp?.length, isCombinationSupplierActive, componentAndPrdocutOptions?.componentSupplier?.length]);

    const compositionMasterOptions = useMemo(() => {
      return SystemMastersDetails?.compositionMasterData?.filter((item) => item.isActive)?.map((item) => ({ label: item?.name, value: item?._id })) || [];
    }, [SystemMastersDetails?.compositionMasterData]);

    const allCompositionMasterData = useMemo(() => {
      return (SystemMastersDetails?.compositionMasterData || []).reduce((acc, item) => {
        acc[item._id] = item;
        return acc;
      }, {});
    }, [SystemMastersDetails?.compositionMasterData]);

    const selectedTierFourCompositionProcessTypes = useMemo(() => {
      const compositionProcessTypeIds = getBomProcessTypeIds(tierFour?.compositionProcessType);
      const selectedOptions = compositionProcessTypeIds
        .map((id) => {
          const data = allCompositionMasterData[id];
          if (data) {
            return { label: data.name, value: data._id };
          }
          return null;
        })
        .filter(Boolean);
      return selectedOptions || [];
    }, [tierFour?.compositionProcessType, allCompositionMasterData]);

    useEffect(() => {
      if (isSuggestionMode) return;
      const cloneState = cloneDeep(state);

      if (BomSuppliers?.length && tier2SupplierOptions?.length && !isCombinationSupplierActive && !id) {
        const findBomSupplierData = tier2SupplierOptions?.find((supplier) => supplier._id === BomSuppliers?.[0]?._id);
        if (findBomSupplierData) {
          set(cloneState, 'tierTwo.componentSupplierObj.componentSupplier', findBomSupplierData._id);
          set(cloneState, 'tierTwo.componentSupplierObj.energySource', findBomSupplierData?.factory?.[0]?.energySource?.value || '');
          set(cloneState, 'tierTwo.componentSupplierObj.energyIntensity', findBomSupplierData?.factory?.[0]?.energyIntensity || '');
          if (cloneState?.tierTwo?.postProcessingAnotherLocation === 'yes') {
            set(cloneState, 'tierTwo.componentPostProcessing.postProcessingSupp', findBomSupplierData._id);
            set(cloneState, 'tierTwo.componentPostProcessing.energySource', findBomSupplierData?.factory?.[0]?.energySource?.value || '');
            set(cloneState, 'tierTwo.componentPostProcessing.energyIntensity', findBomSupplierData?.factory?.[0]?.energyIntensity || '');
          }
          set(cloneState, 'tierOne', null);
          setState((prev) => ({ ...prev, ...cloneState }));
        }
      } else if (isCombinationSupplierActive) {
        set(cloneState, 'tierTwo.componentSupplierObj.componentSupplier', null);
        set(cloneState, 'tierTwo.componentSupplierObj.energySource', '');
        set(cloneState, 'tierTwo.componentSupplierObj.energyIntensity', '');
        if (cloneState?.tierTwo?.postProcessingAnotherLocation === 'yes') {
          set(cloneState, 'tierTwo.componentPostProcessing.postProcessingSupp', null);
          set(cloneState, 'tierTwo.componentPostProcessing.energySource', '');
          set(cloneState, 'tierTwo.componentPostProcessing.energyIntensity', '');
        }
        setState((prev) => ({ ...prev, ...cloneState }));
      }
    }, [BomSuppliers, tier2SupplierOptions?.length, isCombinationSupplierActive, id, isSuggestionMode]);

    useEffect(() => {
      if (isSuggestionMode && BomSuppliers?.length && tier2SupplierOptions?.length) {
        const findBomSupplierData = tier2SupplierOptions?.find((supplier) => supplier._id === BomSuppliers?.[0]?._id);
        if (findBomSupplierData) {
          setState((prev) => {
            const cloneState = cloneDeep(prev);
            set(cloneState, 'tierTwo.componentSupplierObj.componentSupplier', findBomSupplierData?._id);
            set(cloneState, 'tierTwo.componentSupplierObj.energySource', findBomSupplierData?.factories?.[0]?.energySource?.value || '');
            set(cloneState, 'tierTwo.componentSupplierObj.energyIntensity', findBomSupplierData?.factories?.[0]?.energyIntensity || '');
            return cloneState;
          });
        }
      }
    }, [isSuggestionMode, BomSuppliers, tier2SupplierOptions]);

    useEffect(() => {
      if (isSuggestionMode && tier3Suppliers?.length && state?.tierThree?.rawMaterialProcessor?._id) {
        const findBomSupplierData = tier3Suppliers?.find((supplier) => supplier._id === state?.tierThree?.rawMaterialProcessor?._id);
        if (findBomSupplierData) {
          setState((prev) => {
            const cloneState = cloneDeep(prev);
            set(cloneState, 'tierThree.energySource', findBomSupplierData?.factory?.[0]?.energySource?.value || '');
            set(cloneState, 'tierThree.energyIntensity', findBomSupplierData?.factory?.[0]?.energyIntensity || '');
            return cloneState;
          });
        }
      }
    }, [isSuggestionMode, tier3Suppliers, state?.tierThree?.rawMaterialProcessor]);

    useEffect(() => {
      if (isSuggestionMode && tier4Suppliers?.length && state?.tierFour?.rawMaterialExtractor?._id) {
        const findBomSupplierData = tier4Suppliers?.find((supplier) => supplier._id === state?.tierFour?.rawMaterialExtractor?._id);
        if (findBomSupplierData) {
          setState((prev) => {
            const cloneState = cloneDeep(prev);
            set(cloneState, 'tierFour.energySource', findBomSupplierData?.factory?.[0]?.energySource?.value || '');
            set(cloneState, 'tierFour.energyIntensity', findBomSupplierData?.factory?.[0]?.energyIntensity || '');
            return cloneState;
          });
        }
      }
    }, [isSuggestionMode, tier4Suppliers, state?.tierFour?.rawMaterialExtractor]);

    return (
      <div>
        {isCombinationSupplierActive ? (
          <div className="w-300px mb-24">
            <Select
              name="tierOne"
              options={componentAndPrdocutOptions?.productSupplier}
              getOptionLabel={(option) => `${option.name}`}
              getOptionValue={(option) => `${option._id}`}
              styles={SelectTierStyle}
              onChange={(val) => handleChange(val?._id, 'tierOne')}
              placeholder={t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.PLACEHOLDER_TIER_ONE')}
              isClearable
              isDisabled={isReadOnly}
              value={ReferenceSupplier?.find((option) => option._id === tierOne)} // Find the option by _id
              className="common-select"
              classNamePrefix="common-select"
            />
          </div>
        ) : null}
        <p className="text-bold mb-16">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TIER_TWO')}</p>
        <div className="d-flex align-items-center mb-16">
          <p className="mb-0">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.POST_PROCESSING_SENTENCE')} </p>
          <div className="d-flex align-items-center flex-wrap ml-16">
            <div className="custom-radio sm mr-2 ">
              <input
                type="radio"
                id="yes"
                value="yes"
                onChange={(e) => handleChange(e?.target?.value, 'tierTwo.postProcessingAnotherLocation')}
                checked={postProcessingAnotherLocation === 'yes'}
                disabled={isReadOnly}
              />
              <label className="mb-0" htmlFor="yes">
                Yes &nbsp;
              </label>
            </div>
            <div className="custom-radio sm mr-2 ">
              <input
                type="radio"
                id="no"
                value="no"
                onChange={(e) => handleChange(e?.target?.value, 'tierTwo.postProcessingAnotherLocation')}
                checked={postProcessingAnotherLocation === 'no'}
                disabled={isReadOnly}
              />
              <label className="mb-0" htmlFor="no">
                No &nbsp;
              </label>
            </div>
          </div>
        </div>
        {postProcessingAnotherLocation === 'yes' ? (
          <div className="d-flex align-items-center">
            {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.POST_PROCESSING_GOODS_SENTENCE')}{' '}
            <div className="d-flex align-items-center flex-wrap ml-16">
              <div className="custom-radio mr-2 sm">
                <input
                  type="radio"
                  id="componentSupplier"
                  value="componentSupplier"
                  onChange={(e) => handleChange(e?.target?.value, 'tierTwo.finishedProductAssemblySupp')}
                  checked={finishedProductAssemblySupp === 'componentSupplier'}
                  disabled={isReadOnly}
                />
                <label className="mb-0" htmlFor="componentSupplier">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.COMPOENENT_SUPPLIER')} &nbsp;
                </label>
              </div>
              <div className="custom-radio mr-2 sm">
                <input
                  type="radio"
                  id="finishAssemblySupplier"
                  value="finishAssemblySupplier"
                  onChange={(e) => handleChange(e?.target?.value, 'tierTwo.finishedProductAssemblySupp')}
                  checked={finishedProductAssemblySupp === 'finishAssemblySupplier'}
                  disabled={isReadOnly}
                />
                <label className="mb-0" htmlFor="finishAssemblySupplier">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.FINSIH_PRODUCT_ASSEMBLY')} &nbsp;
                </label>
              </div>
            </div>
          </div>
        ) : null}

        <div>
          <p className="text-bold mt-24 mb-16">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.COMPOENENT_SUPPLIER')}</p>
          <Row>
            <Col md={3} lg={2}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.COMPOENENT_SUPPLIER')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="BOMSupplier"
                  options={tier2SupplierOptions}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={
                    tier2SupplierOptions?.find(
                      (supplier) => supplier._id === componentSupplierObj?.componentSupplier?._id || supplier._id === componentSupplierObj?.componentSupplier,
                    ) || null
                  }
                  styles={customStyles}
                  onChange={(val) => handleChange(val?._id, 'tierTwo.componentSupplierObj.componentSupplier', val)}
                  placeholder={t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.COMPOENT_SUPPLIER_PLACEHOLDER')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                />
              </div>
            </Col>
            <Col md={3} lg={2}>
              <div className="common-input-control mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_SOURCE')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={componentSupplierObj?.energySource} />
              </div>
            </Col>{' '}
            <Col md={3} lg={2}>
              <div className="common-input-control mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_INTENSITY')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={componentSupplierObj?.energyIntensity} />
              </div>
            </Col>{' '}
            <Col md={3} lg={3}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">
                  {finishedProductAssemblySupp === 'componentSupplier'
                    ? t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASPORT_POST_PROCESSING_SUPPLIER')
                    : t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASNPORT_COMPONENT_SUPPLIER')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="transport"
                  options={transportArray}
                  getOptionLabel={(option) => `${option.label}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={componentSupplierObj?.transport}
                  styles={customStyles}
                  onChange={(val) => handleChange(val, 'tierTwo.componentSupplierObj.transport')}
                  placeholder={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_TRANSPORT')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                />
              </div>
            </Col>
            <Col md={3} lg={3}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.PROCESSES')}</p>
                <MultiSelect
                  name="bomComponentType"
                  options={componentTypeOptions}
                  value={selectedTierTwoComponentSupplierTypes}
                  onChange={(selected) =>
                    handleChange(
                      (selected || []).map((s) => s.value),
                      'tierTwo.componentSupplierObj.bomProcessType',
                    )
                  }
                  labelledBy={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE')}
                  className="product-custom-multiselect common-new-multiselect"
                  disabled={isReadOnly}
                />
              </div>
            </Col>
          </Row>
        </div>
        {postProcessingAnotherLocation === 'yes' ? (
          <div>
            <p className="text-bold mb-16">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.COMPONENT_POST_PROCESSING')}</p>
            <Row>
              <Col md={3} lg={2}>
                <div className="common-react-select mb-24">
                  <p className="field-name text-bold">
                    {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.POST_PROCESSING_SUPPLIER')}
                    <span className="asterisk asterisk-req-green">*</span>
                  </p>
                  <Select
                    name="BOMSupplier"
                    options={tier2SupplierOptions}
                    getOptionLabel={(option) => `${option.name}`}
                    getOptionValue={(option) => `${option._id}`}
                    value={
                      tier2SupplierOptions?.find(
                        (supplier) => supplier._id === componentPostProcessing?.postProcessingSupp?._id || componentPostProcessing?.postProcessingSupp,
                      ) || null
                    }
                    styles={customStyles}
                    onChange={(val) => handleChange(val?._id, 'tierTwo.componentPostProcessing.postProcessingSupp', val)}
                    placeholder={t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.SELECT_POST_PROCESSING_SUPPLIER_PLACEHOLDER')}
                    isClearable
                    isDisabled={isReadOnly}
                    className="common-select"
                    classNamePrefix="common-select"
                  />
                </div>
              </Col>
              <Col md={3} lg={2}>
                <div className="common-input-control mb-24">
                  <p className="field-name text-bold">
                    {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_SOURCE')}
                    <span className="asterisk asterisk-req-green">*</span>
                  </p>
                  <input readOnly className="form-control" value={componentPostProcessing?.energySource} />
                </div>
              </Col>{' '}
              <Col md={3} lg={2}>
                <div className="common-input-control mb-24">
                  <p className="field-name text-bold">
                    {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_INTENSITY')}
                    <span className="asterisk asterisk-req-green">*</span>
                  </p>
                  <input readOnly className="form-control" value={componentPostProcessing?.energyIntensity} />
                </div>
              </Col>{' '}
              <Col md={3} lg={3}>
                <div className="common-react-select mb-24">
                  <p className="field-name text-bold">
                    {finishedProductAssemblySupp !== 'componentSupplier'
                      ? t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASPORT_POST_PROCESSING_SUPPLIER')
                      : t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASNPORT_COMPONENT_SUPPLIER')}
                    <span className="asterisk asterisk-req-green">*</span>
                  </p>
                  <Select
                    name="transport"
                    options={transportArray}
                    getOptionLabel={(option) => `${option.label}`}
                    getOptionValue={(option) => `${option._id}`}
                    value={componentPostProcessing?.transport}
                    styles={customStyles}
                    onChange={(val) => handleChange(val, 'tierTwo.componentPostProcessing.transport')}
                    placeholder={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_TRANSPORT')}
                    isClearable
                    isDisabled={isReadOnly}
                    className="common-select"
                    classNamePrefix="common-select"
                  />
                </div>
              </Col>
              <Col md={3} lg={3}>
                <div className="common-react-select mb-24">
                  <p className="field-name text-bold">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.PROCESSES')}</p>
                  <MultiSelect
                    name="bomComponentType"
                    options={componentTypeOptions}
                    value={selectedTierTwoComponentPostProcessingTypes}
                    onChange={(selected) =>
                      handleChange(
                        (selected || []).map((s) => s.value),
                        'tierTwo.componentPostProcessing.bomProcessType',
                      )
                    }
                    labelledBy={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE')}
                    className="product-custom-multiselect common-new-multiselect"
                    disabled={isReadOnly}
                  />
                </div>
              </Col>
            </Row>
          </div>
        ) : null}
        <p className="text-bold mb-16">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TIER_THRE')}</p>
        <div className="d-flex align-items-center mb-16">
          <p className="mb-0">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.RAW_MATERIAL_EXTRACTORE')} </p>
          <div className="d-flex align-items-center flex-wrap ml-16">
            <div className="custom-radio mr-2 sm">
              <input
                type="radio"
                id="rawyes"
                value="yes"
                onChange={(e) => handleChange(e?.target?.value, 'tierThree.rawMaterialProcessorExtractor')}
                checked={tierThree?.rawMaterialProcessorExtractor === 'yes'}
                disabled={isReadOnly}
              />
              <label className="mb-0" htmlFor="rawyes">
                Yes &nbsp;
              </label>
            </div>
            <div className="custom-radio mr-2 sm">
              <input
                type="radio"
                id="rawno"
                value="no"
                onChange={(e) => handleChange(e?.target?.value, 'tierThree.rawMaterialProcessorExtractor')}
                checked={tierThree?.rawMaterialProcessorExtractor === 'no'}
                disabled={isReadOnly}
              />
              <label className="mb-0" htmlFor="rawno">
                No &nbsp;
              </label>
            </div>
          </div>
        </div>
        <div>
          <p className="text-bold mb-16">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.RAW_MATERIAL_PROCESSOR')}</p>
          <Row>
            <Col md={3} lg={2}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.RAW_MATERIAL_PROCESSOR')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="BOMSupplier"
                  options={tier3Suppliers}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={
                    tier3Suppliers?.find(
                      (supplier) => supplier._id === tierThree.rawMaterialProcessor?._id || supplier._id === tierThree.rawMaterialProcessor,
                    ) || null
                  }
                  styles={customStyles}
                  onChange={(val) => handleChange(val?._id, 'tierThree.rawMaterialProcessor', val)}
                  placeholder={t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.SELECT_RAW_MATERIAL_PROCESSOR')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                  menuPosition="fixed"
                />
              </div>
            </Col>
            <Col md={3} lg={2}>
              <div className="common-input-control mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_SOURCE')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={tierThree.energySource} />
              </div>
            </Col>{' '}
            <Col md={3} lg={2}>
              <div className="common-input-control mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_INTENSITY')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={tierThree.energyIntensity} />
              </div>
            </Col>{' '}
            <Col md={3} lg={3}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASNPORT_COMPONENT_SUPPLIER_RAW_MATERIAl')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="transport"
                  options={transportArray}
                  getOptionLabel={(option) => `${option.label}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={tierThree.transport}
                  styles={customStyles}
                  onChange={(val) => handleChange(val, 'tierThree.transport')}
                  placeholder={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_TRANSPORT')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                  menuPosition="fixed"
                />
              </div>
            </Col>
            <Col md={3} lg={3}>
              <div className="common-react-select mb-24">
                <p className="field-name text-bold">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.PROCESSES')}</p>
                <MultiSelect
                  name="bomComponentType"
                  options={tierThreeComponentTypeOptions}
                  value={selectedTierThreeComponentTypes}
                  onChange={(selected) =>
                    handleChange(
                      (selected || []).map((s) => s.value),
                      'tierThree.bomProcessType',
                    )
                  }
                  labelledBy={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE')}
                  className="product-custom-multiselect common-new-multiselect"
                  disabled={isReadOnly}
                />
              </div>
            </Col>
          </Row>
        </div>
        <p className="text-bold mb-16"> {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TIER_FOUR')}</p>
        <div>
          <p className="text-bold mb-16"> {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.RAW_MATERIA_EXTRACTOR')}</p>
          <Row>
            <Col md={3} lg={2}>
              <div className="common-react-select mb-0">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.RAW_MATERIA_EXTRACTOR')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="BOMSupplier"
                  options={tier4Suppliers}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={
                    tier4Suppliers?.find((supplier) => supplier._id === tierFour.rawMaterialExtractor?._id || supplier._id === tierFour.rawMaterialExtractor) ||
                    null
                  }
                  styles={customStyles}
                  onChange={(val) => handleChange(val?._id, 'tierFour.rawMaterialExtractor', val)}
                  placeholder={t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.SELECT_RAW_MATERIAL_EXTRACTOR')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                  menuPosition="fixed"
                />
              </div>
            </Col>
            <Col md={3} lg={2}>
              <div className="common-input-control mb-0">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_SOURCE')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={tierFour.energySource} />
              </div>
            </Col>{' '}
            <Col md={3} lg={2}>
              <div className="common-input-control mb-0">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.ENERGY_INTENSITY')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <input readOnly className="form-control" value={tierFour.energyIntensity} />
              </div>
            </Col>{' '}
            <Col md={3} lg={3}>
              <div className="common-react-select mb-0">
                <p className="field-name text-bold">
                  {t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.TRASNPORT_COMPONENT_SUPPLIER_RAW_MATERIAl')}
                  <span className="asterisk asterisk-req-green">*</span>
                </p>
                <Select
                  name="transport"
                  options={transportArray}
                  getOptionLabel={(option) => `${option.label}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={tierFour.transport}
                  styles={customStyles}
                  onChange={(val) => handleChange(val, 'tierFour.transport')}
                  placeholder={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_TRANSPORT')}
                  isClearable
                  isDisabled={isReadOnly}
                  className="common-select"
                  classNamePrefix="common-select"
                  menuPosition="fixed"
                />
              </div>
            </Col>
            <Col md={3} lg={3}>
              <div className="common-react-select placement-top mb-24">
                <p className="field-name text-bold">{t('BOM_LIBRARY.BOM_PRODUCT_IMPACT.PROCESSES')}</p>
                <MultiSelect
                  name="compositionProcessType"
                  options={compositionMasterOptions}
                  value={selectedTierFourCompositionProcessTypes}
                  onChange={(selected) =>
                    handleChange(
                      (selected || []).map((s) => s.value),
                      'tierFour.compositionProcessType',
                    )
                  }
                  labelledBy={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE')}
                  className="product-custom-multiselect common-new-multiselect"
                  disabled={isReadOnly}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  },
);

export default BomProductImpact;
